import { App } from 'hydra-data-types';
import { Environment } from './Environment';

/**
 * Unreleased apps. appVariant does not enforce that values are specified.
 * TODO: Remove Mood before release.
 */
const PRE_RELEASE_APPS = [App.Mood] as const;
type PreReleaseApp = (typeof PRE_RELEASE_APPS)[number];

/** Object where each `App` has a key, and value of type `Value`. Not enforced for pre-release apps.*/
export type VariantMap<Value> = Omit<Record<App, Value>, PreReleaseApp> &
  Partial<Record<PreReleaseApp, Value>>;

/** Returns the value for the current app.
 *
 * To test each app separately, mock this function like so:
 * ```ts
 * import * as AppUtils from '@util/AppUtils';
 * describe.each([['nerva'], ['meno']])('testing app: %s', (appName) => {
 *     jest.spyOn(AppUtils, 'appVariant').mockImplementation((variantMap) => variantMap[appName]);
 *     // assertions
 * });
 * ```
 */
export function appVariant<Value>(values: VariantMap<Value>): Value {
  if (!(Environment.APP in values) && isPreRelease(Environment.APP)) {
    warnOfMissingVariant();
    return values[App.Nerva];
  } else {
    return values[Environment.APP] as Value;
  }
}

type OverrideMap<Value> = Partial<Record<App, Value>> & { default: Value };

/** Returns a specific value for the current app if present, or a default value otherwise */
export function appOverride<Value>(values: OverrideMap<Value>): Value {
  return Environment.APP in values ? (values[Environment.APP] as Value) : values.default;
}

let hasLoggedMissingVariant = false;

function warnOfMissingVariant() {
  if (!hasLoggedMissingVariant) {
    console.warn(
      `No ${Environment.APP} variant exists, falling back to Nerva. (Only logging first occurrence.)`
    );
    hasLoggedMissingVariant = true;
  }
}

/** Avoid using directly, prefer appVariant, feature flags etc. */
function isPreRelease(app: App): boolean {
  return PRE_RELEASE_APPS.includes(app as unknown as PreReleaseApp);
}
